import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TsButtonModule, TsPageHeaderModule, TsPaginatorChangeEvent, TsPaginatorModule } from 'tsui';
import { PaginatedResource } from '@shared/models/paginated-resource';
import { TeamsService } from './services/teams.service';
import { BehaviorSubject, filter, Observable, shareReplay, tap } from 'rxjs';
import { debounceTime, delay, switchMap, take } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { Team } from '@core/models';
import { TeamsGridComponent } from './teams-grid/teams-grid.component';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import {
  TeamsManagementDialogComponent,
  TeamsManagementDialogData,
  TeamsManagementDialogResult,
} from './teams-management-dialog/teams-management-dialog.component';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'teams-settings',
  standalone: true,
  imports: [TsPageHeaderModule, TsButtonModule, TsPaginatorModule, AsyncPipe, TeamsGridComponent],
  templateUrl: './teams.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamsSettingsComponent {
  private readonly teamsService = inject(TeamsService);
  private readonly dialog = inject(Dialog);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);
  private searchTrigger = new BehaviorSubject<{ page: number; per_page: number }>(
    TeamsSettingsComponent.initialPaginationConfig,
  );

  readonly loading = new BehaviorSubject(true);
  teamsPage$: Observable<PaginatedResource<Team>> = this.searchTrigger.pipe(
    debounceTime(500),
    tap(() => this.loading.next(true)),
    switchMap(({ page, per_page }) => {
      return this.teamsService.getTeams(page, per_page);
    }),
    tap(() => this.loading.next(false)),
    shareReplay(),
  );

  static get initialPaginationConfig(): { page: number; per_page: number } {
    return { page: 1, per_page: 20 };
  }

  openTeamsManagementDialog(editableTeam?: Team) {
    const dialogRef: DialogRef<TeamsManagementDialogResult> = this.dialog.open<
      TeamsManagementDialogResult,
      TeamsManagementDialogData
    >(TeamsManagementDialogComponent, {
      data: {
        editableTeam,
      },
      ...TsDialogComponent.getDefaultDialogConfig(),
    });

    dialogRef.closed
      .pipe(
        filter((result) => !!result),
        tap(() => this.loading.next(true)),
        switchMap((result) => {
          return !editableTeam
            ? this.teamsService.createTeam(result)
            : this.teamsService.editTeam(result, editableTeam?.id);
        }),
        take(1),
      )
      .subscribe(() => {
        this.searchTrigger.next(this.searchTrigger.value);
      });
  }

  pageChanged({ activePage, pageSize }: TsPaginatorChangeEvent) {
    this.searchTrigger.next({
      page: activePage,
      per_page: pageSize,
    });
  }

  onOpenEditDialog(editableTeam: Team) {
    this.openTeamsManagementDialog(editableTeam);
  }

  onOpenDeleteDialog({ name, parent_id, id }: Team) {
    if (!parent_id) return;

    this.confirmationDialogService
      .openConfirmationDialog({
        dialogTitle: `Delete ${name}`,
        ...ConfirmationDialogService.deleteConfirmationCommonLayoutConfig(),
      })
      .pipe(
        filter((result) => result),
        tap(() => this.loading.next(true)),
        switchMap(() => this.teamsService.deleteTeam(id)),
        take(1),
      )
      .subscribe(() => {
        this.searchTrigger.next(this.searchTrigger.value);
      });
  }
}
